/**
 * Client-side keyframe extraction (Codex design).
 *
 * The video pipeline ships candidate frame *times* (`Step.keyframeCandidatesMs`) from the
 * backend; the browser decodes the real pixels so the picker shows true frames without a
 * server round-trip per candidate. Everything runs against a single `<video>` + reusable
 * `<canvas>`, one seek at a time (decoders choke on parallel seeks), and is defensive about
 * iOS Safari mp4 quirks (moov-at-end → wait for both `loadedmetadata` and `seeked`).
 */

/**
 * Attach `src` to an offscreen `<video>` and resolve once metadata (dimensions/duration) is
 * known. `muted` + `playsInline` keep iOS happy; `crossOrigin='anonymous'` keeps the canvas
 * untainted so `toBlob` works on Range-served media.
 */
export function loadVideo(src: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("document unavailable"));
      return;
    }
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.crossOrigin = "anonymous";
    // Keep it out of the layout/a11y tree; it only feeds the canvas.
    video.setAttribute("aria-hidden", "true");

    const cleanup = () => {
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("error", onError);
    };
    const onMeta = () => {
      cleanup();
      resolve(video);
    };
    const onError = () => {
      cleanup();
      reject(new Error(video.error ? `video error (${video.error.code})` : "video load failed"));
    };

    video.addEventListener("loadedmetadata", onMeta, { once: true });
    video.addEventListener("error", onError, { once: true });
    video.src = src;
    // Nudge the load (some browsers defer until in-DOM otherwise).
    video.load();
  });
}

/** Reused across one extraction pass so we don't churn canvases per frame. */
let sharedCanvas: HTMLCanvasElement | null = null;
function canvas(): HTMLCanvasElement {
  if (!sharedCanvas) sharedCanvas = document.createElement("canvas");
  return sharedCanvas;
}

/**
 * Seek `video` to `seconds`, wait for the frame to actually decode (`seeked`), and return a
 * JPEG blob of that frame. Resolves serially — callers must `await` one before the next so the
 * decoder isn't asked to seek to two places at once.
 *
 * Throws on a 0×0 frame (metadata not ready / decode failed) or a `toBlob` miss.
 */
export function extractFrame(video: HTMLVideoElement, seconds: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const duration = Number.isFinite(video.duration) ? video.duration : undefined;
    // Clamp into range; iOS rejects seeks past duration. ±0.5s tolerance per Codex.
    const target = Math.max(0, duration != null ? Math.min(seconds, Math.max(0, duration - 0.05)) : seconds);

    let settled = false;
    const cleanup = () => {
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onError);
    };
    const fail = (msg: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(msg));
    };
    const onError = () => fail("video error during seek");

    const onSeeked = () => {
      if (settled) return;
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) {
        fail("frame has no dimensions (0x0)");
        return;
      }
      const c = canvas();
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      if (!ctx) {
        fail("2d context unavailable");
        return;
      }
      try {
        ctx.drawImage(video, 0, 0, w, h);
      } catch (e) {
        fail((e as Error).message || "drawImage failed");
        return;
      }
      c.toBlob(
        (blob) => {
          if (settled) return;
          settled = true;
          cleanup();
          if (blob) resolve(blob);
          else reject(new Error("toBlob returned null"));
        },
        "image/jpeg",
        0.85,
      );
    };

    video.addEventListener("seeked", onSeeked);
    video.addEventListener("error", onError, { once: true });

    // If we're already at the target time, no `seeked` fires — sample on the next frame.
    if (Math.abs(video.currentTime - target) < 0.01) {
      // Force a tiny delta so a `seeked` is guaranteed, then snap back is unnecessary.
      requestAnimationFrame(() => onSeeked());
    } else {
      video.currentTime = target;
    }
  });
}

/** A decoded candidate frame: a JPEG blob + an object URL for display. Revoke when done. */
export interface ExtractedFrame {
  timecodeMs: number;
  blob: Blob;
  url: string;
}

/**
 * Extract a list of frame times (ms) serially. `onProgress(done, total)` fires after each
 * frame so the picker can show extraction progress. Individual failures are skipped (the
 * picker pads/handles gaps) rather than aborting the whole pass.
 */
export async function extractFrames(
  video: HTMLVideoElement,
  timesMs: number[],
  onProgress?: (done: number, total: number) => void,
): Promise<ExtractedFrame[]> {
  const out: ExtractedFrame[] = [];
  for (let i = 0; i < timesMs.length; i++) {
    const ms = timesMs[i];
    try {
      const blob = await extractFrame(video, ms / 1000);
      out.push({ timecodeMs: ms, blob, url: URL.createObjectURL(blob) });
    } catch {
      // Skip the frame; keep going so the rest of the step's candidates still render.
    }
    onProgress?.(i + 1, timesMs.length);
  }
  return out;
}

/** Revoke object URLs created by {@link extractFrames}. Safe to call with any list. */
export function revokeFrames(frames: { url: string }[]): void {
  for (const f of frames) {
    if (f.url) URL.revokeObjectURL(f.url);
  }
}

/** Release the underlying media element so the browser can reclaim the decoder. */
export function disposeVideo(video: HTMLVideoElement | null): void {
  if (!video) return;
  try {
    video.pause();
  } catch {
    /* not playing */
  }
  video.removeAttribute("src");
  try {
    video.load();
  } catch {
    /* best-effort */
  }
}
